import { InputHTMLAttributes, useRef } from "react";
import JsonView from "@uiw/react-json-view";
import JsonViewEditor from "@uiw/react-json-view/editor";
import { type SemicolonProps, useHighlight } from "@uiw/react-json-view";

type EditOpts = {
  value: unknown;
  oldValue: unknown;
  keyName?: string | number;
  parentName?: string | number;
  namespace?: Array<string | number>;
  type?: "value" | "key";
};

function applyEdit(
  obj: object,
  opts: EditOpts
): { updated_src: object } | null {
  const { value, oldValue, namespace = [], type } = opts;
  if (typeof obj !== "object" || obj === null) return null;
  const updated = JSON.parse(JSON.stringify(obj)) as Record<string, unknown>;

  if (namespace.length === 0) return null;

  if (type === "key") {
    const parentPath = namespace.slice(0, -1);
    const oldKey = String(namespace[namespace.length - 1] ?? oldValue);
    const newKey = String(value);
    let parent: Record<string, unknown> = updated;
    for (let i = 0; i < parentPath.length; i++) {
      const key = parentPath[i];
      parent = parent[key] as Record<string, unknown>;
      if (!parent || typeof parent !== "object") return null;
    }
    if (oldKey in parent) {
      parent[newKey] = parent[oldKey];
      delete parent[oldKey];
    }
  } else {
    let current: Record<string, unknown> = updated;
    for (let i = 0; i < namespace.length - 1; i++) {
      const key = String(namespace[i]);
      current = current[key] as Record<string, unknown>;
      if (!current || typeof current !== "object") return null;
    }
    const lastKey = String(namespace[namespace.length - 1]);
    current[lastKey] = value;
  }

  return { updated_src: updated };
}

export function JsonViewer({
  data = { data: "No data to display" },
  onEdit,
  onAdd,
  ...props
}: {
  data?: any;
  onEdit?: (e: { updated_src: object }) => void;
  onAdd?: (e: any) => void;
} & Omit<InputHTMLAttributes<HTMLInputElement>, "onChange">) {
  const ObjectKey: SemicolonProps["render"] = ({
    value,
    keyName,
    parentName,
    ...props
  }) => {
    const $edit = useRef<HTMLSpanElement & HTMLModElement>(null);
    useHighlight({ value, highlightUpdates: true, highlightContainer: $edit });
    if (keyName === "integer" && typeof value === "number" && value > 40) {
      return <del {...props} ref={$edit} />;
    }
    return <span {...props} ref={$edit} />;
  };

  return (
    <div className="z-0 h-full w-full">
      {onEdit ? (
        <JsonViewEditor
          value={data}
          keyName="root"
          onEdit={(opts) => {
            const result = applyEdit(data, opts);
            if (result) {
              onEdit(result);
            }
            return true;
          }}
          components={{
            objectKey: ObjectKey,
          }}
        />
      ) : (
        <JsonView
          value={data}
          style={{ minHeight: "100%", width: "100%" }}
          enableClipboard={true}
          displayDataTypes={false}
          displayObjectSize={false}
          indentWidth={4}
          collapsed={1}
        />
      )}
    </div>
  );
}
